import {DomainAncestor, entityConfig, fieldInfo} from "core/common/domain/DomainAncestor";
import {observable, ObservableMap} from "mobx";
import {LovItem} from "core/metamodel/mdEntity";
import {ID, ISECURABLE, MODULE, PERMISSION, PRIMITIVE, SECURABLE, SECURABLE_TYPE, TSECURABLE} from "core/metamodel/metaConstant";
import {Layouts} from "react-grid-layout";
import {IDomainObject, lovValue} from "core/form/formUtils";
import {InvoiceVatRecap, INVOICEWARNING} from "invoice/domain/invoiceDomain";

@entityConfig()
export class SecurablePermissionDetail extends DomainAncestor {

  @observable detailId: string;

  @observable readPermission: boolean;
  @observable newPermission: boolean;
  @observable updatePermission: boolean;
  @observable deletePermission: boolean;
  @observable executePermission: boolean;
  @observable exportPermission: boolean;
  @observable importPermission: boolean;


  constructor(json: IDomainObject & { [key: string]: any }) {
    super(json);
  }

  public getPermissionList(): PERMISSION[] {
    return [...(this.readPermission ? [PERMISSION.Read] : []),
      ...(this.newPermission ? [PERMISSION.New] : []),
      ...(this.updatePermission ? [PERMISSION.Update] : []),
      ...(this.deletePermission ? [PERMISSION.Delete] : []),
      ...(this.executePermission ? [PERMISSION.Execute] : []),
      ...(this.exportPermission ? [PERMISSION.Export] : []),
      ...(this.importPermission ? [PERMISSION.Import] : [])
    ];
  }

  public isPermission(permission: PERMISSION): boolean {
    switch (permission) {
      case PERMISSION.Read:
        return this.readPermission;
      case PERMISSION.New:
        return this.newPermission;
      case PERMISSION.Update:
        return this.updatePermission;
      case PERMISSION.Delete:
        return this.deletePermission;
      case PERMISSION.Execute:
        return this.executePermission;
      case PERMISSION.Export:
        return this.exportPermission;
      case PERMISSION.Import:
        return this.importPermission;
    }
  }

  public setPermission(permission: PERMISSION, value: boolean): void {
    switch (permission) {
      case PERMISSION.Read:
        this.readPermission = value;
        break;
      case PERMISSION.New:
        this.newPermission = value;
        break;
      case PERMISSION.Update:
        this.updatePermission = value;
        break;
      case PERMISSION.Delete:
        this.deletePermission = value;
        break;
      case PERMISSION.Execute:
        this.executePermission = value;
        break;
      case PERMISSION.Export:
        this.exportPermission = value;
        break;
      case PERMISSION.Import:
        this.importPermission = value;
        break;
    }
  }

}

@entityConfig()
export class SecurablePermission extends DomainAncestor {

  @observable securityRole: LovItem<string>;
  @observable securable: LovItem<TSECURABLE>;

  @observable readPermission: boolean;
  @observable newPermission: boolean;
  @observable updatePermission: boolean;
  @observable deletePermission: boolean;
  @observable executePermission: boolean;
  @observable exportPermission: boolean;
  @observable importPermission: boolean;
  @observable publicGridPermission: boolean;
  @observable tenantGroupGridPermission: boolean;
  @observable tenantGridPermission: boolean;

  module: MODULE;
  type: SECURABLE_TYPE;

  @observable @fieldInfo(SecurablePermissionDetail) detailPermissions?: ObservableMap<SecurablePermissionDetail>;

  constructor(json: any) {
    super(json);
  }

  public fromJson(json: any): this {
    super.fromJson(json, "detailPermissions");
    if (!this.detailPermissions) this.detailPermissions = new ObservableMap<SecurablePermissionDetail>();
    return this;
  }

  public getPermissionList(): PERMISSION[] {
    return [...(this.readPermission ? [PERMISSION.Read] : []),
      ...(this.newPermission ? [PERMISSION.New] : []),
      ...(this.updatePermission ? [PERMISSION.Update] : []),
      ...(this.deletePermission ? [PERMISSION.Delete] : []),
      ...(this.executePermission ? [PERMISSION.Execute] : []),
      ...(this.exportPermission ? [PERMISSION.Export] : []),
      ...(this.importPermission ? [PERMISSION.Import] : []),
      ...(this.publicGridPermission ? [PERMISSION.PublicGrid] : []),
      ...(this.tenantGridPermission ? [PERMISSION.TenantGrid] : []),
      ...(this.tenantGroupGridPermission ? [PERMISSION.TenantGroupGrid] : []),
    ];
  }

  public isPermission(permission: PERMISSION): boolean {
    switch (permission) {
      case PERMISSION.Read:
        return this.readPermission;
      case PERMISSION.New:
        return this.newPermission;
      case PERMISSION.Update:
        return this.updatePermission;
      case PERMISSION.Delete:
        return this.deletePermission;
      case PERMISSION.Execute:
        return this.executePermission;
      case PERMISSION.Export:
        return this.exportPermission;
      case PERMISSION.Import:
        return this.importPermission;
      case PERMISSION.PublicGrid:
        return this.publicGridPermission;
      case PERMISSION.TenantGrid:
        return this.tenantGridPermission;
      case PERMISSION.TenantGroupGrid:
        return this.tenantGroupGridPermission;
    }
  }

  public modifyPermissions(permissions: PERMISSION[]): SecurablePermission {
    this.readPermission = permissions.includes(PERMISSION.Read);
    this.newPermission = permissions.includes(PERMISSION.New);
    this.updatePermission = permissions.includes(PERMISSION.Update);
    this.deletePermission = permissions.includes(PERMISSION.Delete);
    this.executePermission = permissions.includes(PERMISSION.Execute);
    this.exportPermission = permissions.includes(PERMISSION.Export);
    this.importPermission = permissions.includes(PERMISSION.Import);
    this.publicGridPermission = permissions.includes(PERMISSION.PublicGrid);
    this.tenantGridPermission = permissions.includes(PERMISSION.TenantGrid);
    this.tenantGroupGridPermission = permissions.includes(PERMISSION.TenantGroupGrid);
    return this;
  }

  public setPermission(permission: PERMISSION, value: boolean): void {
    switch (permission) {
      case PERMISSION.Read:
        this.readPermission = value;
        break;
      case PERMISSION.New:
        this.newPermission = value;
        break;
      case PERMISSION.Update:
        this.updatePermission = value;
        break;
      case PERMISSION.Delete:
        this.deletePermission = value;
        break;
      case PERMISSION.Execute:
        this.executePermission = value;
        break;
      case PERMISSION.Export:
        this.exportPermission = value;
        break;
      case PERMISSION.Import:
        this.importPermission = value;
        break;
      case PERMISSION.PublicGrid:
        this.publicGridPermission = value;
        break;
      case PERMISSION.TenantGrid:
        this.tenantGridPermission = value;
        break;
      case PERMISSION.TenantGroupGrid:
        this.tenantGroupGridPermission = value;
        break;
    }
  }

}

export type TPermissionMap = Partial<{ [key in TSECURABLE]: SecurablePermission }>;


export interface SecurableDefinition {
  module: LovItem<MODULE>;
  type: LovItem<SECURABLE_TYPE>;
  securable: LovItem<TSECURABLE>;
  availablePermissions: PERMISSION[];
  detailPermissions: LovItem<string>[];
}

export type SecurableDefinitionSecurableMap = Partial<{ [key in TSECURABLE]: SecurableDefinition }>
export type SecurableDefinitionModuleMap = Partial<{ [key in MODULE]: SecurableDefinitionSecurableMap }>


@entityConfig()
export class UserSession extends DomainAncestor {
  @observable username: string;
  @observable email: string;
  @observable firstName: string;
  @observable lastName: string;
  @observable titleBefore: string;
  @observable titleAfter: string;
  @observable position: string;
  @observable internalLine: string;
  @observable mobile: string;
  @observable hrNumber: string;
  @observable displayName: string;
  @observable partyId: number;


  layoutConfig: Layouts;

  @observable permissionMap: TPermissionMap;
  @observable restrictions: RESTRICTION[];

  @observable entityPreferences: Record<string, { confirmDelete: boolean, extendedPreferences: ExtendedPreferences }>

  @observable dashboardItems: any[];

  constructor(json: any) {
    super(json);
    if (!this.restrictions) this.restrictions = [];
    if (!this.entityPreferences) this.entityPreferences = {};
  }

  public fromJson(json: any): this {
    super.fromJson(json);
    if (this.permissionMap) {
      this.permissionMap = Object.entries(this.permissionMap).reduce((acc, [key, spJson]) => {
        acc[key] = new SecurablePermission(spJson);
        return acc;
      }, {});
    }
    return this;
  }

  permissionList(securable: TSECURABLE): PERMISSION[] {
    if (this.permissionMap) {
      let securablePermission = this.permissionMap[securable];
      if (securablePermission) {
        return securablePermission.getPermissionList();
      }
    }
    return null;
  }

  detailIdList(securable: TSECURABLE): string[] {
    if (this.permissionMap) {
      let securablePermission = this.permissionMap[securable];
      if (securablePermission) {
        return securablePermission.detailPermissions.keys();
      }
    }
    return [];
  }


  hasRight(securable: TSECURABLE, permission: PERMISSION, detailId?: string): boolean {
    if (this.permissionMap) {
      let securablePermission = this.permissionMap[securable];
      if (securablePermission) {
        const securableConfig: ISECURABLE = SECURABLE[securable];
        if (securableConfig && securableConfig.detailHeader && ![PERMISSION.PublicGrid, PERMISSION.TenantGrid, PERMISSION.TenantGroupGrid].includes(permission)) {
          if (detailId) {
            let securablePermissionDetail = securablePermission.detailPermissions.get(detailId);
            return (securablePermissionDetail) ? securablePermissionDetail.isPermission(permission) : false;
          } else {
            return securablePermission.detailPermissions.values().some(spd => spd.isPermission(permission));
          }
        } else {
          return securablePermission.isPermission(permission);
        }
      }
    }
    return false;
  }


  hasRestriction(restriction: RESTRICTION): boolean {
    return this.restrictions.includes(restriction);
  }

  confirmDelete(entityName: string): boolean {
    let entityPref = this.entityPreferences[entityName];
    return (entityPref) ? entityPref.confirmDelete : false;
  }

  extendedPreferences(entityName: string): ExtendedPreferences {
    let entityPref = this.entityPreferences[entityName];
    return (entityPref) ? entityPref.extendedPreferences : null;
  }


  public getId(): ID {
    return this.username;
  }

  public getMePartyLov(): LovItem {
    return {value: this.partyId, label: this.displayName};
  }


}


@entityConfig()
export class Tenant extends DomainAncestor {

  @observable name: string;
  @observable tenantGroup: LovItem;
  @observable party: LovItem;
  @observable localCurrency: LovItem;
  @observable defaultCurrency: LovItem;
  @observable vatRateCountry: LovItem;
  @observable monetaryRounding: number;

  constructor(json: any) {
    super(json);
  }
}


@entityConfig()
export class UserToken extends DomainAncestor {

  @observable tokenId: string;
  @observable tokenString: string;
  @observable tokenName: string;
  @observable user: LovItem;
  @observable closeDate: string | Date;
  @observable validFrom: string | Date;
  @observable validTo: string | Date;

  constructor(json: any) {
    super(json);
  }
}

export enum RESTRICTION {
  YARD_MGMT_HAULER_VEHICLE = "YARD_MGMT_HAULER_VEHICLE",
  YARD_MGMT_HAULER_DRIVER = "YARD_MGMT_HAULER_DRIVER",
  LIMIT_PARTY_BY_USER_PROFITCENTER = "LIMIT_PARTY_BY_USER_PROFITCENTER",
  LIMIT_VEHICLE_BY_USER_PROFITCENTER = "LIMIT_VEHICLE_BY_USER_PROFITCENTER",
  LIMIT_PRODUCT_BY_USER_PROFITCENTER = "LIMIT_PRODUCT_BY_USER_PROFITCENTER",
  LIMIT_INVOICE_BY_USER_PROFITCENTER = "LIMIT_INVOICE_BY_USER_PROFITCENTER",
  SIMPLE_SHIPMENT_VISIT = "SIMPLE_SHIPMENT_VISIT"
}


@entityConfig()
export class User extends DomainAncestor {

  @observable username: string;

  @observable enabled: boolean;
  @observable locked: boolean;
  @observable expired: boolean;

  @observable password1: string;
  @observable password2: string;
  @observable oldPassword: string;

  @observable rememberMe: boolean;
  @observable expirationMinutes: number;
  @observable passwordPolicy: LovItem;
  @observable accountExpiration: string;

  @observable defaultTenant: number;
  @observable party: LovItem;
  @observable permissionMap: TPermissionMap;
  @observable restrictions: LovItem[];
  @observable gridConfigs: number[];
  @observable tenants: number[];
  @observable securityRoles: LovItem[];
  @observable profitCenters: LovItem[];
  @observable defaultGridConfig: { [key: string]: number };
  _copiedFromUsername: string;

  @observable @fieldInfo(UserToken) tokens: UserToken[];

  constructor(json: any) {
    super(json);

    if (!this.gridConfigs) this.gridConfigs = [];
    if (!this.tenants) this.tenants = [];
    if (!this.securityRoles) this.securityRoles = [];
    if (!this.profitCenters) this.profitCenters = [];
    if (!this.defaultGridConfig) this.defaultGridConfig = {};
    if (!this.permissionMap) this.permissionMap = {};
  }

  public fromJson(json: any): this {
    super.fromJson(json);
    this.permissionMap = (this.permissionMap) ? Object.entries(this.permissionMap).reduce((acc, [key, spJson]) => {
      acc[key] = new SecurablePermission(spJson);
      return acc;
    }, {}) : {};
    return this;
  }

  public getId(): ID {
    return this.username;
  }

  public setId(id: ID): void {
    this.username = id as any;
  }

}

export enum PARAMKEY {
  BATCH_INVOICE_SPLIT_PROFIT_CENTER = "BATCH_INVOICE_SPLIT_PROFIT_CENTER",
  PREFERRED_RATE_SOURCE = "PREFERRED_RATE_SOURCE",
  GDPR_LOGIN_TEXT = "GDPR_LOGIN_TEXT",
}

@entityConfig()
export class RestrictionData extends DomainAncestor {
  @observable employer?: LovItem;
  @observable userProfitCenterIds?: ID[];
  @observable lastComingTime: string | Date;
  @observable lastLeavingTime: string | Date;

  constructor(json: any) {
    super(json);
  }


  public showComing(): boolean {
    return (!this.lastComingTime || !!this.lastLeavingTime);
  }

}

export interface UserContextDto {
  userSession?: UserSession;
  tenant?: Tenant;
  availableTenants?: LovItem[];
  serverRestartTime?: string;
  tenantParams?: { [TKey in PARAMKEY]: PRIMITIVE }
  restrictionData?: RestrictionData;
}

@entityConfig()
export class ExtendedPreferences extends DomainAncestor {
  @observable enabledInvoiceWarnings: INVOICEWARNING[];

  constructor(json: any) {
    super(json);
    if (!this.enabledInvoiceWarnings) this.enabledInvoiceWarnings = [];
  }
}


@entityConfig()
export class UserEntityPreference extends DomainAncestor {
  @observable user: LovItem;
  @observable entityName: string;
  @observable confirmDelete: boolean;
  @observable @fieldInfo(ExtendedPreferences) extendedPreferences: ExtendedPreferences;


  constructor(json: any) {
    super(json);
  }

  public fromJson(json: any): this {
    super.fromJson(json);
    if (!this.extendedPreferences) this.extendedPreferences = new ExtendedPreferences({});
    return this;
  }

}


@entityConfig()
export class UserProfileDto extends DomainAncestor {
  @observable @fieldInfo(User) user: User;
  @observable @fieldInfo(UserEntityPreference) entityPreferences: UserEntityPreference[];

  constructor(json: any) {
    super(json);
    if (!this.entityPreferences) this.entityPreferences = [];
  }

}




@entityConfig()
export class SecurityRole extends DomainAncestor {

  @observable roleId: string;
  @observable roleName: string;
  @observable comment: string;
  @observable users: LovItem[];
  @observable @fieldInfo(SecurablePermission) permissions: SecurablePermission[];

  @observable restrictions: LovItem[];


  constructor(json: any) {
    super(json);

    if (!this.users) this.users = [];
    if (!this.permissions) this.permissions = [];
  }

  public getId(): ID {
    return this.roleId;
  }

  public setId(id: ID): void {
    this.roleId = id as any;
  }

}



// WEBPACK FOOTER //
// ./src/core/security/securityDomain.ts